import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Play, CheckCircle2, AlertTriangle, Clock, Award, Star } from "lucide-react";

const inputCls = "bg-white border-steel text-charcoal h-11";
const LEVEL_STYLES = {
  developing: "bg-amber-100 border-amber-300 text-amber-800",
  consistent: "bg-sky-100 border-sky-300 text-sky-800",
  competent: "bg-emerald-100 border-emerald-300 text-emerald-800",
};
const SAFETY_LABELS = {
  incidents: "Incidents", near_misses: "Near misses", machine_damage: "Machine damage",
  repeated_defects: "Repeated defects", attendance: "Attendance", reliability: "Reliability",
  rams_compliance: "RAMS compliance", defect_reporting: "Defect reporting",
  timesheet_compliance: "Timesheet compliance",
};
const DECISION_LABELS = {
  competent: "Approved — Lomond Competent Operator",
  continue_development: "Continue Development",
  hold: "Management Hold / Further Review",
};

function Evidence({ label, value, testid }) {
  return (
    <div className="bg-elevated rounded-sm p-3">
      <p className="text-sm font-semibold text-charcoal" data-testid={testid}>{value ?? "—"}</p>
      <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function CompetenceReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManagement = user?.role === "management_admin";
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [decision, setDecision] = useState(null);
  const [decForm, setDecForm] = useState({ effective_date: "", pay_rate_change: "", continue_reasons: "", new_objectives: "", next_review_date: "" });
  const [saving, setSaving] = useState("");

  const load = () => api.get(`/staff/operators/${id}/review`)
    .then((r) => {
      setData(r.data);
      const rv = r.data.review;
      if (rv) setForm({
        competence_comments: rv.competence_comments || {}, safety_summary: rv.safety_summary || {},
        questions: rv.questions || {}, management_comments: rv.management_comments || "",
        strengths: rv.strengths || "", development_areas: rv.development_areas || "",
        pay_rate: r.data.evidence.pay_rate || "", pay_rate_change: rv.pay_rate_change || "",
      });
    })
    .catch(() => toast.error("Could not load review"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="review-loading" />;
  const { review, reviews, evidence: ev } = data;
  const completed = reviews.filter((r) => r.status === "completed");

  const startReview = async () => {
    try {
      await api.post(`/staff/operators/${id}/review/start`);
      toast.success("Competence review started.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveDraft = async () => {
    setSaving("draft");
    try {
      await api.put(`/staff/operators/${id}/review`, form);
      toast.success("Review draft saved.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const decide = async () => {
    setSaving("decide");
    try {
      await api.post(`/staff/operators/${id}/review/decide`, {
        decision,
        management_comments: form.management_comments,
        strengths: form.strengths,
        development_areas: form.development_areas,
        ...decForm,
      });
      toast.success(decision === "competent" ? "Approved — Lomond Competent Operator." : decision === "continue_development" ? "Continue Development recorded." : "Review placed on hold.");
      setDecision(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const answeredCount = form ? Object.keys(form.questions).length : 0;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="competence-review-page">
      <Button variant="outline" size="sm" onClick={() => navigate(`/staff/operators/${id}`)} className="border-steel text-charcoal bg-white" data-testid="back-to-operator">
        <ArrowLeft className="h-4 w-4 mr-1" /> Operator Profile
      </Button>

      <section className="bg-charcoal text-white rounded-md p-6" data-testid="review-header">
        <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond">Formal Competence Review</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl mt-1" data-testid="review-operator-name">{ev.operator.name}</h1>
        <p className="text-white/60 text-xs font-mono mt-1">
          {ev.operator.operator_grade === "development" ? "Qualified Development Operator" : ev.operator.operator_grade === "competent" ? "Lomond Competent Operator" : "Operator"}
          {" — "}started {ev.employment_start_date} — week {ev.weeks_employed}
        </p>
      </section>

      {/* Evidence pack — always visible, auto-populated */}
      <section className="bg-white border border-steel rounded-md p-5" data-testid="evidence-pack">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm mb-4 flex items-center gap-2">
          <Award className="h-4 w-4 text-lomond" /> Automatic Evidence Summary
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Evidence label="Qualification" value={ev.qualification ? `${ev.qualification.course_name} — ${(ev.qualification.result || "").toUpperCase()}` : "—"} testid="ev-qualification" />
          <Evidence label="Development period" value={`${ev.weeks_employed} weeks`} testid="ev-weeks" />
          <Evidence label="Assignments" value={ev.assignments.count} testid="ev-assignments" />
          <Evidence label="Feedback records" value={ev.feedback.count} testid="ev-feedback-count" />
        </div>

        <div className="mt-5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Total development seat hours: <span className="text-charcoal font-bold text-sm" data-testid="ev-seat-total">{ev.seat_hours.total}h</span></p>
          <div className="flex gap-2 flex-wrap" data-testid="ev-seat-categories">
            {Object.entries(ev.seat_hours.by_category).map(([cat, h]) => (
              <span key={cat} className="bg-elevated border border-steel rounded-sm px-3 py-1.5 text-xs font-mono">{cat} — <strong>{h}h</strong></span>
            ))}
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Customers</p>
            <div className="flex gap-1.5 flex-wrap">{ev.assignments.customers.map((c) => <span key={c} className="text-xs bg-elevated border border-steel rounded-sm px-2 py-1">{c}</span>)}</div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Sites</p>
            <div className="flex gap-1.5 flex-wrap">{ev.assignments.sites.map((s) => <span key={s} className="text-xs bg-elevated border border-steel rounded-sm px-2 py-1">{s}</span>)}</div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Work types</p>
            <div className="flex gap-1.5 flex-wrap" data-testid="ev-work-types">{ev.assignments.work_types.map((w) => <span key={w} className="text-xs bg-elevated border border-steel rounded-sm px-2 py-1">{w}</span>)}</div>
          </div>
        </div>

        <div className="mt-5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Supervisor / customer feedback averages ({ev.feedback.count} records — evidence, not an automatic trigger)</p>
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-2" data-testid="ev-feedback-avgs">
            {Object.entries(ev.feedback.averages).map(([f, v]) => (
              <div key={f} className="bg-elevated rounded-sm p-2.5 text-center">
                <p className="font-heading font-black text-lg text-charcoal">{v}</p>
                <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">{f.replace(/_/g, " ")}</p>
              </div>
            ))}
            {!Object.keys(ev.feedback.averages).length && <p className="text-xs text-slate-400 col-span-7">No feedback records yet.</p>}
          </div>
          {ev.feedback.recent_comments.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {ev.feedback.recent_comments.map((c, i) => (
                <p key={i} className="text-xs text-slate-600 border-l-2 border-lomond pl-2.5 flex items-start gap-1.5">
                  <Star className="h-3 w-3 text-lomond shrink-0 mt-0.5" />"{c.comments}" — {c.by}{c.customer ? ` (${c.customer})` : ""}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="ev-plans">
          {ev.plans.map((p) => (
            <div key={p.plan_number} className={`rounded-md border p-3.5 ${p.plan_number === 3 ? "border-lomond border-2 bg-lomond/5" : "border-steel bg-elevated"}`} data-testid={`ev-plan-${p.plan_number}`}>
              <p className="text-xs font-bold text-charcoal uppercase">Action Plan {p.plan_number} — {p.period_label}{p.plan_number === 3 && <span className="text-lomond ml-1">(competence preparation)</span>}</p>
              <p className="text-xs text-slate-600 mt-1">{p.status === "completed" ? "Completed" : "In progress"} — {p.objectives_achieved}/{p.objectives_total} objectives achieved</p>
              {p.outcome && <p className="text-xs text-slate-600 mt-0.5">Outcome: {p.outcome.replace(/_/g, " ")}</p>}
              {p.outstanding.length > 0 && <p className="text-xs text-amber-700 mt-0.5">Outstanding: {p.outstanding.join("; ")}</p>}
              {p.manager_comments && <p className="text-xs text-slate-500 mt-1 italic">"{p.manager_comments}"</p>}
            </div>
          ))}
        </div>
      </section>

      {!review && (
        <section className="bg-white border border-steel rounded-md p-5 space-y-4" data-testid="review-not-started">
          {isManagement ? (
            <Button onClick={startReview} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="start-review-btn">
              <Play className="h-4 w-4 mr-1.5" /> Start Competence Review
            </Button>
          ) : (
            <p className="text-sm text-slate-500">A competence review is started by management once the operator is flagged ready.</p>
          )}
          {completed.length > 0 && (
            <div className="space-y-2" data-testid="review-history">
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Previous reviews</p>
              {completed.map((r) => (
                <div key={r.id} className="bg-elevated rounded-md p-3.5 text-sm" data-testid={`review-history-${r.id}`}>
                  <p className="font-semibold text-charcoal">{r.decision_label} — {r.decision_date}</p>
                  {r.snapshot && <p className="text-xs text-slate-500 mt-0.5">Snapshot: {r.snapshot.seat_hours?.total}h — {r.snapshot.assignments} assignments — week {r.snapshot.weeks_employed}</p>}
                  {r.continue_reasons && <p className="text-xs text-slate-600 mt-1">Reasons: {r.continue_reasons} — next review {r.next_review_date}</p>}
                  {r.management_comments && <p className="text-xs text-slate-600 mt-1 italic">"{r.management_comments}"</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {review && form && (
        <>
          <div className="bg-amber-50 border border-amber-300 rounded-md px-4 py-3 flex items-center gap-2" data-testid="review-in-progress">
            <Clock className="h-4 w-4 text-amber-600" />
            <p className="text-sm text-amber-800 font-semibold">Review in progress — started by {review.started_by}, {new Date(review.started_at).toLocaleString("en-GB")}</p>
          </div>

          <section className="bg-white border border-steel rounded-md p-5 space-y-4" data-testid="safety-summary">
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Safety / Compliance Summary <span className="text-slate-400 font-normal normal-case">(summarised from Connecteam evidence — not duplicated)</span></h2>
            <div className="grid sm:grid-cols-3 gap-2.5">
              {ev.safety_fields.map((f) => (
                <div key={f} className="space-y-1">
                  <label className="text-xs text-slate-500">{SAFETY_LABELS[f]}</label>
                  <Input value={form.safety_summary[f] || ""} disabled={!isManagement}
                    onChange={(e) => setForm({ ...form, safety_summary: { ...form.safety_summary, [f]: e.target.value } })}
                    className={inputCls} data-testid={`safety-${f}`} placeholder="e.g. None" />
                </div>
              ))}
            </div>
            <div className="flex gap-2" data-testid="safety-overall">
              {[["no_concerns", "Safety Record: No concerns"], ["management_review_required", "Management Review Required"]].map(([v, l]) => (
                <button key={v} type="button" disabled={!isManagement}
                  onClick={() => setForm({ ...form, safety_summary: { ...form.safety_summary, overall: v } })}
                  className={`flex-1 px-3 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide transition-all ${
                    form.safety_summary.overall === v ? (v === "no_concerns" ? "bg-emerald-500 text-white border-emerald-500" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-600 border-steel hover:border-lomond"
                  }`} data-testid={`safety-overall-${v}`}>{l}</button>
              ))}
            </div>
          </section>

          <section className="bg-white border border-steel rounded-md p-5 space-y-3" data-testid="competence-areas">
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Competence Areas — review each one</h2>
            {ev.competence.map((c) => (
              <div key={c.key} className="bg-elevated rounded-md p-3 space-y-2" data-testid={`review-comp-${c.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-charcoal">{c.label}</span>
                  <span className={`px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${LEVEL_STYLES[c.level]}`}>{c.level}</span>
                </div>
                {isManagement && (
                  <Input value={form.competence_comments[c.key] || ""}
                    onChange={(e) => setForm({ ...form, competence_comments: { ...form.competence_comments, [c.key]: e.target.value } })}
                    placeholder="Comment (optional)" className="bg-white border-steel text-charcoal h-9 text-xs" data-testid={`review-comp-comment-${c.key}`} />
                )}
              </div>
            ))}
          </section>

          <section className="bg-white border border-steel rounded-md p-5 space-y-3" data-testid="review-questions">
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Management Review Questions <span className="text-slate-400 font-normal">({answeredCount}/{ev.questions.length})</span></h2>
            {ev.questions.map((q, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-elevated rounded-md p-3.5" data-testid={`question-${i}`}>
                <p className="text-sm text-charcoal flex-1">{i + 1}. {q}</p>
                <div className="flex gap-1.5 shrink-0">
                  {[["YES", true], ["NO", false]].map(([l, v]) => (
                    <button key={l} type="button" disabled={!isManagement}
                      onClick={() => setForm({ ...form, questions: { ...form.questions, [`q${i}`]: v } })}
                      className={`px-4 py-2.5 rounded-md border text-xs font-black tracking-wide transition-all min-w-14 ${
                        form.questions[`q${i}`] === v ? (v ? "bg-emerald-500 text-white border-emerald-500" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-600 border-steel hover:border-lomond"
                      }`} data-testid={`question-${i}-${l.toLowerCase()}`}>{l}</button>
                  ))}
                </div>
              </div>
            ))}
          </section>

          <section className="bg-white border border-steel rounded-md p-5 space-y-3" data-testid="review-comments">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-charcoal">Management comments</label>
                <Textarea value={form.management_comments} disabled={!isManagement} onChange={(e) => setForm({ ...form, management_comments: e.target.value })} className="bg-white border-steel text-charcoal" data-testid="review-mgmt-comments" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-charcoal">Strengths</label>
                <Textarea value={form.strengths} disabled={!isManagement} onChange={(e) => setForm({ ...form, strengths: e.target.value })} className="bg-white border-steel text-charcoal" data-testid="review-strengths" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-charcoal">Development areas</label>
                <Textarea value={form.development_areas} disabled={!isManagement} onChange={(e) => setForm({ ...form, development_areas: e.target.value })} className="bg-white border-steel text-charcoal" data-testid="review-dev-areas" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm text-slate-600">Current pay rate (if held)</label>
                <Input value={form.pay_rate} disabled={!isManagement} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} className={inputCls} data-testid="review-pay-rate" />
              </div>
            </div>
            {isManagement && (
              <Button onClick={saveDraft} disabled={saving === "draft"} variant="outline" className="border-steel text-charcoal bg-white" data-testid="review-save-draft">
                {saving === "draft" ? "Saving..." : "Save Draft"}
              </Button>
            )}
          </section>

          {isManagement && (
            <section className="bg-white border-2 border-lomond rounded-md p-5 space-y-4" data-testid="decision-zone">
              <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">Final Decision — a management decision based on the whole evidence pack</h2>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => setDecision(decision === "competent" ? null : "competent")}
                  className={`font-heading font-extrabold uppercase ${decision === "competent" ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"}`} data-testid="decide-competent-btn">
                  Approve as Lomond Competent Operator
                </Button>
                <Button onClick={() => setDecision(decision === "continue_development" ? null : "continue_development")}
                  className={`font-heading font-extrabold uppercase ${decision === "continue_development" ? "bg-amber-500 text-black" : "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200"}`} data-testid="decide-continue-btn">
                  Continue Development
                </Button>
                <Button onClick={() => setDecision(decision === "hold" ? null : "hold")} variant="outline"
                  className="border-steel text-slate-600 bg-white font-heading font-extrabold uppercase" data-testid="decide-hold-btn">
                  Management Hold / Further Review
                </Button>
              </div>

              {decision === "competent" && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-md p-4 space-y-3" data-testid="decide-competent-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><label className="text-xs text-slate-600">Effective date (default today)</label>
                      <Input type="date" value={decForm.effective_date} onChange={(e) => setDecForm({ ...decForm, effective_date: e.target.value })} className={inputCls} data-testid="decide-effective-date" /></div>
                    <div className="space-y-1"><label className="text-xs text-slate-600">Pay rate change (if applicable)</label>
                      <Input value={decForm.pay_rate_change} onChange={(e) => setDecForm({ ...decForm, pay_rate_change: e.target.value })} placeholder="e.g. £18.50/hr" className={inputCls} data-testid="decide-pay-change" /></div>
                  </div>
                  <p className="text-xs text-slate-500">Grade changes to LOMOND COMPETENT OPERATOR. Decision date, approver, comments and effective date are recorded. All development evidence is preserved.</p>
                </div>
              )}
              {decision === "continue_development" && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-3" data-testid="decide-continue-form">
                  <p className="text-xs text-amber-800">Not a failure — grade stays Qualified Development Operator. Reasons, new objectives and next review date are required.</p>
                  <Textarea value={decForm.continue_reasons} onChange={(e) => setDecForm({ ...decForm, continue_reasons: e.target.value })} placeholder="Reasons (required)" className="bg-white border-amber-300 text-charcoal" data-testid="decide-reasons" />
                  <Textarea value={decForm.new_objectives} onChange={(e) => setDecForm({ ...decForm, new_objectives: e.target.value })} placeholder="New development objectives (required) — e.g. more trenching experience, improve grading finish, more evidence on larger excavator" className="bg-white border-amber-300 text-charcoal" data-testid="decide-objectives" />
                  <div className="space-y-1"><label className="text-xs text-slate-600">Next review date (required)</label>
                    <Input type="date" value={decForm.next_review_date} onChange={(e) => setDecForm({ ...decForm, next_review_date: e.target.value })} className={`${inputCls} max-w-xs`} data-testid="decide-next-review" /></div>
                </div>
              )}

              {decision && (
                <Button onClick={decide} disabled={saving === "decide"}
                  className="bg-charcoal text-white font-heading font-extrabold uppercase hover:bg-black w-full sm:w-auto" data-testid="decide-confirm-btn">
                  {saving === "decide" ? "Recording..." : `Confirm: ${DECISION_LABELS[decision]}`}
                </Button>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
